import { useEffect, useMemo, useRef, useState } from "react";
import { FaUserPlus } from "react-icons/fa";
import { MdClose, MdErrorOutline, MdFilterList, MdOutlineInbox, MdRefresh, MdSearch } from "react-icons/md";
import BoxAlert from '../../components/boxAlert/index.jsx';
import Header from "../../components/header/index.jsx";
import { Loading } from "../../components/loading";
import LeadMenu from "./components/leadMenu/index.jsx";
import LeadsList from "./components/leadsList/index.jsx";
import RegisterDialog from "./components/registerDialog/index.jsx";
import styles from "./css/leads.module.css";
import { getLeads } from "./services/getLeads.js";



// FILTROS DA LISTA: `match` DECIDE SE O LEAD ENTRA EM CADA OPÇÃO
const CAPTED_FROM = (origin) => (lead) => String(lead.capted_from).toLowerCase() == origin;

const SITUATION_FILTERS = [
  { value: "all", label: "Todos", match: () => true },
  { value: "converted", label: "Convertidos", match: (lead) => lead.active },
];

const ORIGIN_FILTERS = [
  { value: "all", label: "Todas", match: () => true },
  { value: "instagram", label: "Instagram", match: CAPTED_FROM("instagram") },
  { value: "facebook", label: "Facebook", match: CAPTED_FROM("facebook") },
  { value: "whatsapp", label: "Whatsapp", match: CAPTED_FROM("whatsapp") },
  { value: "sofchef", label: "SofChef", match: CAPTED_FROM("sofchef") },
  { value: "cmv", label: "CMV", match: CAPTED_FROM("cmv") },
];

// CAMPOS CONSIDERADOS NA BUSCA
const SEARCH_FIELDS = ["name", "document", "email", "phone"];

// NORMALIZA O TEXTO PARA A BUSCA (SEM ACENTOS E SEM DIFERENCIAR MAIÚSCULAS/MINÚSCULAS)
const normalize = (text) => (text ?? "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();


export default function Leads() {

  const [leads, setLeads] = useState([]);
  const [showLeadMenu, setShowLeadMenu] = useState(false);
  const selectedLead = useRef(null);
  const selectedLeadToEdit = useRef(null);
  const [warnAlert, setWarnAlert] = useState({ show: false, title: null, text: null });
  const [showRegisterDialod, setShowRegisterDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // FILTROS DA TELA
  const [situationFilter, setSituationFilter] = useState("all");
  const [originFilter, setOriginFilter] = useState("all");
  const [search, setSearch] = useState("");


  useEffect(() => {
    handleGetleads();
  }, []);


  const handleGetleads = async () => {
    try{
      setLoading(true);
      setLoadError(false);

      const res = await getLeads();

      // GARANTE UMA LISTA, MESMO SE A API RESPONDER COM OUTRO FORMATO
      setLeads(Array.isArray(res) ? res : []);

    } catch (e) {
      console.error('ERROR - INIT SCREEN:', e);
      setLoadError(true);

    } finally {
      setLoading(false);
    }
  }


  const activeSituation = SITUATION_FILTERS.find((filter) => filter.value == situationFilter);
  const activeOrigin = ORIGIN_FILTERS.find((filter) => filter.value == originFilter);

  // LEADS QUE BATEM COM A BUSCA. OS CONTADORES DOS CHIPS PARTEM DESTA LISTA
  const searched = useMemo(() => {
    const term = normalize(search).trim();

    if (!term) return leads;

    return leads.filter((lead) => SEARCH_FIELDS.some((field) => normalize(lead?.[field]).includes(term)));
  }, [leads, search]);

  // LISTA EXIBIDA: SEMPRE DERIVADA DOS LEADS, DA BUSCA E DOS FILTROS ATIVOS
  const leadsRender = useMemo(
    () => searched.filter((lead) => activeSituation.match(lead) && activeOrigin.match(lead)),
    [searched, situationFilter, originFilter]
  );

  const hasActiveFilter = situationFilter != "all" || originFilter != "all" || search.trim() != "";

  const clearFilters = () => {
    setSituationFilter("all");
    setOriginFilter("all");
    setSearch("");
  };


  const handleOpenLead = (lead) => {
    selectedLead.current = lead;

    setShowLeadMenu(true);
  }

  const handleReturnFromRegisterDialog = async () => {
    await handleGetleads();
    selectedLeadToEdit.current = null;
    setShowRegisterDialog(false);
  }



  // SCREEN ====================================>
  return (
    <div>

      <Header>
        <h2 className={styles.titleCompaniesArea} style={{ color: '#fff' }}>Leads</h2>
      </Header>

      <div className={styles.container}>
        <div className={styles.leadsArea}>

          {/* FILTROS */}
          {!loading && !loadError && (
            <div className={styles.filtersArea}>
              <div className={styles.filtersHeader}>
                <h3><MdFilterList size={18} /> Filtrar leads</h3>

                <div className={styles.filtersActions}>
                  {hasActiveFilter && (
                    <button className={styles.clearFilters} onClick={clearFilters}>Limpar filtros</button>
                  )}

                  <button className={styles.registerButton} onClick={() => setShowRegisterDialog(true)}>
                    <FaUserPlus size={16} /> Registrar lead
                  </button>
                </div>
              </div>

              {leads.length > 0 && (
                <>
                  <div className={styles.searchBar}>
                    <MdSearch size={20} className={styles.searchIcon} />

                    <input
                      className={styles.searchInput}
                      type="text"
                      value={search}
                      placeholder="Nome, e-mail, telefone ou CPF/CNPJ"
                      onChange={({ target: { value } }) => setSearch(value)}
                    />

                    {search != "" && (
                      <button className={styles.searchClear} onClick={() => setSearch("")} aria-label="Limpar busca">
                        <MdClose size={18} />
                      </button>
                    )}
                  </div>

                  <div className={styles.filterGroup}>
                    <span className={styles.filterLabel}>Situação</span>

                    <div className={styles.chipsRow}>
                      {SITUATION_FILTERS.map((filter) => (
                        <button
                          key={filter.value}
                          className={`${styles.chip} ${situationFilter == filter.value ? styles.chipActive : ""}`}
                          onClick={() => setSituationFilter(filter.value)}
                        >
                          {filter.label} <span className={styles.chipCount}>{searched.filter((lead) => filter.match(lead) && activeOrigin.match(lead)).length}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className={styles.filterGroup}>
                    <span className={styles.filterLabel}>Origem</span>

                    <div className={styles.chipsRow}>
                      {ORIGIN_FILTERS.map((filter) => (
                        <button
                          key={filter.value}
                          className={`${styles.chip} ${originFilter == filter.value ? styles.chipActive : ""}`}
                          onClick={() => setOriginFilter(filter.value)}
                        >
                          {filter.label} <span className={styles.chipCount}>{searched.filter((lead) => filter.match(lead) && activeSituation.match(lead)).length}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}


          {/* LISTA */}
          <div className={styles.leadsTableArea}>
            {!loading && !loadError && leads.length > 0 && (
              <div className={styles.listHeader}>
                <span className={styles.listCount}>
                  {leadsRender.length == leads.length ? `${leads.length} leads` : `${leadsRender.length} de ${leads.length} leads`}
                </span>
              </div>
            )}

            {loading ? (
              <Loading />

            ) : loadError ? (
              <div className={styles.stateBox}>
                <div className={`${styles.stateIcon} ${styles.bad}`}><MdErrorOutline size={30} /></div>
                <h3>Não foi possível carregar os leads</h3>
                <p>Verifique a sua conexão de internet ou entre em contato com o TI para averiguar a situação.</p>

                <button className={styles.stateButton} onClick={handleGetleads}>
                  <MdRefresh size={18} /> Tentar novamente
                </button>
              </div>

            ) : leads.length == 0 ? (
              <div className={styles.stateBox}>
                <div className={`${styles.stateIcon} ${styles.total}`}><MdOutlineInbox size={30} /></div>
                <h3>Nenhum lead registrado</h3>
                <p>Os leads captados aparecerão aqui. Você também pode registrar um lead manualmente.</p>
              </div>

            ) : leadsRender.length == 0 ? (
              <div className={styles.stateBox}>
                <div className={`${styles.stateIcon} ${styles.total}`}><MdOutlineInbox size={30} /></div>
                <h3>Nenhum lead encontrado</h3>
                <p>Nenhum lead corresponde à busca e aos filtros aplicados.</p>

                <button className={styles.stateButton} onClick={clearFilters}>Limpar filtros</button>
              </div>

            ) : (
              <LeadsList
                clients={leadsRender}
                openLead={(lead) => handleOpenLead(lead)}
              />
            )}
          </div>

        </div>
      </div>



      {/* <=============== COMPONENTS CONTROL ===============> */}

      <LeadMenu
        view={showLeadMenu}
        data={selectedLead.current}
        refreshList={() => handleGetleads()}
        openEditor={() => {
          selectedLeadToEdit.current = selectedLead.current;
          setShowRegisterDialog(true);
        }}
        close={() => { 
          setShowLeadMenu(false);
        }}
      />


      <RegisterDialog
        view={showRegisterDialod}
        onClose={() => {
          selectedLeadToEdit.current = null;
          setShowRegisterDialog(false);
        }}
        onReturn={() => handleReturnFromRegisterDialog()}
        lead={selectedLeadToEdit.current}
      />


      {/* <=============== ALERTS CONTROL ===============> */}


      {/* WARN ALERT */}

      <BoxAlert
        show={warnAlert.show}
        title={warnAlert.title}
        text={warnAlert.text}
        onClose={() => setWarnAlert({ show: false, title: null, text: null })}
      />

    </div>
  );
}
